<script setup lang="ts">
  import {
    ICreateOptionDto,
    IUpdateOptionDto,
    IUpdateOptionItemDto,
  } from '@/api/services/options.service';
  import ControlNumberInput from '@/components/control/ControlNumberInput.vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import SingleImageUploader from '@/components/SingleImageUploader.vue';
  import { OptionType } from '@/enums/OptionType';
  import { Globals } from '@/modules/Globals';
  import { computed, inject, onMounted, Ref, ref } from 'vue';

  const optionData = inject<Ref<ICreateOptionDto | IUpdateOptionDto>>(
    'optionData'
  );

  const uploadsPath = Globals.uploadsPath;

  onMounted(() => {
    if (optionData.value.optionItems.length === 0) {
      addOptionItem();
      addOptionItem();
    }
  });

  const isAddType = computed(() => optionData.value.type === OptionType.Add);

  /** "Se puede repetir": the dumb-friendly face of the underlying option type. */
  const repeatable = computed({
    get: () => optionData.value.type === OptionType.Add,
    set: (value: boolean) => {
      if (value) {
        optionData.value.type = OptionType.Add;
        optionData.value.multiple = true;
        optionData.value.mandatory = false;
      } else {
        optionData.value.type = OptionType.Choose;
      }
    },
  });

  const expandedIds = ref<number[]>([]);

  function toggleExpanded(optionItem: IUpdateOptionItemDto) {
    const idx = expandedIds.value.indexOf(optionItem.id);
    if (idx > -1) expandedIds.value.splice(idx, 1);
    else expandedIds.value.push(optionItem.id);
  }

  /** The whole row toggles expansion, except clicks on its inputs/buttons. */
  function onRowClick(optionItem: IUpdateOptionItemDto, event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (target.closest('input, textarea, button, label')) return;
    toggleExpanded(optionItem);
  }

  function isExpanded(optionItem: IUpdateOptionItemDto) {
    return expandedIds.value.includes(optionItem.id);
  }

  function addOptionItem() {
    optionData.value.optionItems.push(<IUpdateOptionItemDto>{
      id: -Math.round(Math.random() * 1000000000),
      title: '',
      description: null,
      picture: null,
      cost: 0,
      discountedCost: null,
      active: true,
      index: optionData.value.optionItems.length,
    });
  }

  /** Reorders over the VISIBLE (alive, sorted) list so soft-deleted rows never swallow a move. */
  function moveOptionItem(optionItem: IUpdateOptionItemDto, direction: -1 | 1) {
    const alive = aliveOptionItems.value;
    const pos = alive.indexOf(optionItem);
    const targetPos = pos + direction;
    if (pos === -1 || targetPos < 0 || targetPos >= alive.length) return;

    const reordered = [...alive];
    reordered.splice(pos, 1);
    reordered.splice(targetPos, 0, optionItem);
    reordered.forEach((itm, idx) => {
      itm.index = idx;
    });
  }

  function moveOptionItemUp(optionItem: IUpdateOptionItemDto) {
    moveOptionItem(optionItem, -1);
  }

  function moveOptionItemDown(optionItem: IUpdateOptionItemDto) {
    moveOptionItem(optionItem, 1);
  }

  function removeOptionItem(optionItem: IUpdateOptionItemDto) {
    const index = optionData.value.optionItems.indexOf(optionItem);

    if (optionItem.id && optionItem.id > 0) {
      optionItem.deletedAt = new Date();
    } else {
      optionData.value.optionItems.splice(index, 1);
    }
  }

  const aliveOptionItems = computed(() => {
    return (optionData.value.optionItems as IUpdateOptionItemDto[])
      .filter((item) => !item.deletedAt)
      .sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
  });

  const activeCount = computed(
    () => aliveOptionItems.value.filter((item) => item.active).length
  );

  /** Mirrors the backend rule: the last active choice can't be deactivated. */
  function isLastActive(optionItem: IUpdateOptionItemDto) {
    return optionItem.active && activeCount.value === 1;
  }

  function toggleActive(optionItem: IUpdateOptionItemDto) {
    if (isLastActive(optionItem)) return;
    optionItem.active = !optionItem.active;
  }

  function thumbnailUrl(optionItem: IUpdateOptionItemDto): string | null {
    if (!optionItem.picture) return null;
    return `${uploadsPath}/${optionItem.picture}`;
  }
</script>

<template>
  <div class="option-basic">
    <!-- Option-level fields -->
    <div class="group-fields">
      <label class="field">
        <span class="field-label">{{ $t('optionEditor.nameLabel') }}</span>
        <input
          v-model="optionData.title"
          class="field-input"
          type="text"
          maxlength="255"
        />
      </label>
      <label class="field">
        <span class="field-label">
          {{ $t('optionEditor.descriptionLabel') }}
        </span>
        <textarea
          v-model="optionData.description"
          class="field-textarea"
          rows="2"
          :placeholder="$t('optionEditor.descriptionPlaceholder')"
        />
      </label>
    </div>

    <!-- Settings -->
    <div class="settings">
      <span class="section-title">{{ $t('optionEditor.settings') }}</span>
      <div class="settings-grid">
        <div class="setting">
          <v-checkbox
            v-model="repeatable"
            :label="$t('optionEditor.repeatable')"
            class="setting-checkbox"
            dense
            hide-details
          />
          <span class="setting-help">
            {{ $t('optionEditor.repeatableHelp') }}
          </span>
        </div>
        <!-- Repeatable options (add-ons) are never mandatory (the API enforces it too) -->
        <div v-if="!isAddType" class="setting">
          <v-checkbox
            v-model="optionData.mandatory"
            :label="$t('optionEditor.mandatory')"
            class="setting-checkbox"
            dense
            hide-details
          />
          <span class="setting-help">
            {{ $t('optionEditor.mandatoryHelp') }}
          </span>
        </div>
        <div class="setting">
          <template v-if="!isAddType">
            <v-checkbox
              v-model="optionData.multiple"
              :label="$t('optionEditor.multiple')"
              class="setting-checkbox"
              dense
              hide-details
            />
            <span class="setting-help">
              {{ $t('optionEditor.multipleHelp') }}
            </span>
          </template>
          <div v-if="optionData.multiple || isAddType" class="min-max">
            <div class="min-max-field">
              <span class="min-max-label">{{ $t('optionEditor.min') }}</span>
              <ControlNumberInput v-model="optionData.min" />
            </div>
            <div class="min-max-field">
              <span class="min-max-label">{{ $t('optionEditor.max') }}</span>
              <ControlNumberInput v-model="optionData.max" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Choices -->
    <div class="choices">
      <span class="section-title">{{ $t('optionEditor.choices') }}</span>
      <div class="choices-list">
        <span v-if="aliveOptionItems.length === 0" class="empty-message">
          {{ $t('optionEditor.emptyChoices') }}
        </span>
        <div
          v-for="optionItem in aliveOptionItems"
          :key="optionItem.id"
          class="choice"
          :class="{ expanded: isExpanded(optionItem) }"
        >
          <div class="choice-row" @click="onRowClick(optionItem, $event)">
            <div class="choice-thumb">
              <img
                v-if="thumbnailUrl(optionItem)"
                class="choice-thumb-img"
                :src="thumbnailUrl(optionItem)"
                alt=""
              />
              <PosUiIcon
                v-else
                name="image-outline"
                class="choice-thumb-placeholder"
              />
            </div>
            <span
              class="choice-active-dot"
              :class="{ inactive: !optionItem.active }"
            />
            <div class="choice-title-wrap">
              <input
                v-model="optionItem.title"
                class="choice-title-input"
                type="text"
                maxlength="255"
                :placeholder="$t('optionEditor.choiceName')"
              />
              <span
                v-if="!isExpanded(optionItem) && optionItem.description"
                class="choice-description-preview"
              >
                {{ optionItem.description }}
              </span>
            </div>
            <input
              v-model="optionItem.cost"
              class="choice-cost-input"
              type="number"
              :placeholder="$t('optionEditor.choicePrice')"
            />
            <div class="choice-actions">
              <button
                class="choice-icon-button"
                :disabled="aliveOptionItems[0] === optionItem"
                @click="moveOptionItemUp(optionItem)"
              >
                <PosUiIcon name="arrow-up" />
              </button>
              <button
                class="choice-icon-button"
                :disabled="
                  aliveOptionItems[aliveOptionItems.length - 1] === optionItem
                "
                @click="moveOptionItemDown(optionItem)"
              >
                <PosUiIcon name="arrow-down" />
              </button>
              <button
                class="choice-icon-button expand-button"
                @click="toggleExpanded(optionItem)"
              >
                <PosUiIcon
                  :name="isExpanded(optionItem) ? 'chevron-up' : 'chevron-down'"
                />
              </button>
            </div>
          </div>

          <div v-show="isExpanded(optionItem)" class="choice-details">
            <div class="choice-details-grid">
              <div class="choice-picture">
                <span class="detail-label">
                  {{ $t('optionEditor.choicePicture') }}
                </span>
                <SingleImageUploader
                  v-model="optionItem.picture"
                  :maxWidth="800"
                  :maxHeight="800"
                />
              </div>
              <div class="choice-detail-fields">
                <label class="field">
                  <span class="field-label">
                    {{ $t('optionEditor.choiceDescription') }}
                  </span>
                  <textarea
                    v-model="optionItem.description"
                    class="field-textarea"
                    rows="2"
                    :placeholder="$t('optionEditor.descriptionPlaceholder')"
                  />
                </label>
                <div class="choice-detail-actions">
                  <div class="active-toggle">
                    <v-switch
                      :input-value="optionItem.active"
                      :disabled="isLastActive(optionItem)"
                      :label="$t('optionEditor.choiceActive')"
                      class="active-switch"
                      color="green"
                      dense
                      hide-details
                      @change="toggleActive(optionItem)"
                    />
                    <span v-if="isLastActive(optionItem)" class="setting-help">
                      {{ $t('optionEditor.lastActiveHint') }}
                    </span>
                    <span v-else class="setting-help">
                      {{ $t('optionEditor.choiceActiveHelp') }}
                    </span>
                  </div>
                  <PosUiButton
                    color="error"
                    variant="soft"
                    size="sm"
                    icon="delete-outline"
                    @click="removeOptionItem(optionItem)"
                  >
                    {{ $t('optionEditor.deleteChoice') }}
                  </PosUiButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <PosUiButton
        color="neutral"
        variant="outline"
        icon="plus"
        class="add-choice-button"
        @click="addOptionItem"
      >
        {{ $t('optionEditor.addChoice') }}
      </PosUiButton>
    </div>
  </div>
</template>

<style scoped lang="scss">
  // Spacing uses margins (space-x/y), not flex gap: old POS hardware
  // (Chrome < 84). Grid gap is fine.
  .option-basic {
    @apply tw-flex tw-flex-col tw-space-y-5;

    .field {
      @apply tw-flex tw-flex-col tw-space-y-1;

      .field-label {
        @apply tw-text-xs tw-font-medium tw-text-neutral-500;
      }

      .field-input {
        @apply tw-h-11 tw-w-full tw-rounded-lg tw-border tw-border-solid tw-border-neutral-300 tw-bg-surface tw-px-3 tw-text-sm tw-text-neutral-700 tw-outline-none tw-transition-colors placeholder:tw-text-neutral-400;

        &:focus {
          @apply tw-border-orange-600 tw-ring-1 tw-ring-orange-600;
        }
      }

      .field-textarea {
        @apply tw-w-full tw-resize-none tw-rounded-lg tw-border tw-border-solid tw-border-neutral-300 tw-bg-surface tw-px-3 tw-py-2 tw-text-sm tw-text-neutral-700 tw-outline-none tw-transition-colors placeholder:tw-text-neutral-400;

        &:focus {
          @apply tw-border-orange-600 tw-ring-1 tw-ring-orange-600;
        }
      }
    }

    .group-fields {
      @apply tw-flex tw-flex-col tw-space-y-3;
    }

    .section-title {
      @apply tw-text-sm tw-font-semibold tw-text-neutral-900;
    }

    .settings {
      @apply tw-flex tw-flex-col tw-space-y-2;

      .settings-grid {
        @apply tw-grid tw-grid-cols-1 tw-gap-3 sm:tw-grid-cols-2;

        .setting {
          @apply tw-rounded-xl tw-border tw-border-neutral-200 tw-p-3;

          .setting-checkbox {
            @apply tw-m-0 tw-p-0;
          }
        }
      }
    }

    .setting-help {
      @apply tw-text-xs tw-text-neutral-500;
    }

    .min-max {
      @apply tw-mt-2 tw-flex tw-space-x-3;

      .min-max-field {
        @apply tw-flex tw-flex-col tw-space-y-1;

        .min-max-label {
          @apply tw-text-xs tw-font-medium tw-text-neutral-500;
        }
      }
    }

    .choices {
      @apply tw-flex tw-flex-col tw-space-y-2;

      .choices-list {
        @apply tw-flex tw-flex-col tw-space-y-2;

        .empty-message {
          @apply tw-rounded-xl tw-border tw-border-dashed tw-border-neutral-300 tw-py-4 tw-text-center tw-text-sm tw-text-neutral-500;
        }

        .choice {
          @apply tw-rounded-xl tw-border tw-border-neutral-200 tw-bg-surface tw-transition-shadow;

          &.expanded {
            @apply tw-shadow-sm;
          }

          .choice-row {
            // Two rows on phones (name gets the full width), one row from
            // `sm` up. 640px is Tailwind's `sm`, matching the component's
            // sm:tw-* utilities.
            @apply tw-grid tw-cursor-pointer tw-items-center tw-gap-2 tw-p-2;
            grid-template-columns: auto auto minmax(0, 1fr) auto;
            grid-template-areas:
              'thumb dot title title'
              'thumb dot cost actions';

            @media (min-width: 640px) {
              // The modal shrink-wraps to its content, so the flexible title
              // track needs an intrinsic minimum — with minmax(0, 1fr) the
              // whole row (and the modal) would collapse to its fixed parts.
              grid-template-columns: auto auto minmax(12rem, 1fr) auto auto;
              grid-template-areas: 'thumb dot title cost actions';
            }

            .choice-thumb {
              grid-area: thumb;
              @apply tw-flex tw-h-10 tw-w-10 tw-flex-none tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-lg tw-bg-neutral-100;

              .choice-thumb-img {
                @apply tw-h-full tw-w-full tw-object-cover;
              }

              .choice-thumb-placeholder {
                @apply tw-text-xl tw-text-neutral-400;
              }
            }

            .choice-active-dot {
              @apply tw-h-2 tw-w-2 tw-flex-none tw-rounded-full tw-bg-emerald-500;
              grid-area: dot;

              &.inactive {
                @apply tw-bg-neutral-300;
              }
            }

            .choice-title-wrap {
              @apply tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-space-y-0.5;
              grid-area: title;

              .choice-title-input {
                @apply tw-h-11 tw-w-full tw-cursor-text tw-rounded-lg tw-border tw-border-solid tw-border-neutral-300 tw-bg-surface tw-px-3 tw-text-sm tw-text-neutral-700 tw-outline-none tw-transition-colors placeholder:tw-text-neutral-400;

                &:focus {
                  @apply tw-border-orange-600 tw-ring-1 tw-ring-orange-600;
                }
              }

              .choice-description-preview {
                @apply tw-truncate tw-px-1 tw-text-xs tw-text-neutral-500;
              }
            }

            .choice-cost-input {
              @apply tw-h-11 tw-w-24 tw-flex-none tw-cursor-text tw-rounded-lg tw-border tw-border-solid tw-border-neutral-300 tw-bg-surface tw-px-3 tw-text-sm tw-tabular-nums tw-text-neutral-900 tw-outline-none tw-transition-colors;
              grid-area: cost;
              justify-self: start;

              @media (min-width: 640px) {
                justify-self: auto;
              }

              &:focus {
                @apply tw-border-orange-600 tw-ring-1 tw-ring-orange-600;
              }
            }

            .choice-actions {
              @apply tw-flex tw-flex-none tw-items-center;
              grid-area: actions;
              justify-self: end;

              .choice-icon-button {
                @apply tw-flex tw-h-11 tw-w-9 tw-items-center tw-justify-center tw-rounded-lg tw-text-lg tw-text-neutral-500 tw-transition-colors;

                &:hover {
                  @apply tw-bg-neutral-100 tw-text-neutral-900;
                }

                &:disabled {
                  @apply tw-cursor-not-allowed tw-text-neutral-300;

                  &:hover {
                    @apply tw-bg-transparent tw-text-neutral-300;
                  }
                }

                &:focus-visible {
                  @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
                }
              }
            }
          }

          .choice-details {
            @apply tw-border-t tw-border-neutral-200 tw-p-3;

            .choice-details-grid {
              @apply tw-flex tw-flex-col sm:tw-flex-row;

              .choice-picture {
                // The uploader's root is a Vuetify .row (negative margins):
                // the label carries the space, not a sibling rule.
                @apply tw-flex tw-w-full tw-flex-none tw-flex-col sm:tw-w-40;

                .detail-label {
                  @apply tw-mb-1 tw-text-xs tw-font-medium tw-text-neutral-500;
                }
              }

              .choice-detail-fields {
                // Below the picture on phones, beside it from sm.
                @apply tw-mt-3 tw-flex tw-w-full tw-flex-col tw-space-y-3 sm:tw-ml-3 sm:tw-mt-0;

                .choice-detail-actions {
                  @apply tw-flex tw-items-end tw-justify-between tw-space-x-3;

                  .active-toggle {
                    @apply tw-flex tw-flex-col tw-space-y-0.5;

                    .active-switch {
                      @apply tw-m-0 tw-p-0;
                    }
                  }
                }
              }
            }
          }
        }
      }

      .add-choice-button {
        @apply tw-self-start;
      }
    }
  }
</style>
